import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import ChecklistForm from './ChecklistForm';
import Footer from './Footer';
import { Shield } from 'lucide-react';
import SuggestVehicleModal from './SuggestVehicleModal';
import { useToast } from './Toast';

function Home({ user }) {
  const [dominio, setDominio] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [iniciarChecklist, setIniciarChecklist] = useState(false);
  const [showFluidos, setShowFluidos] = useState(true);
  const [searchResult, setSearchResult] = useState(null);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const toast = useToast();

  const handleStartChecklist = async () => {
    if (dominio.trim() === '') {
      toast.warning('Ingresá un dominio para iniciar.');
      return;
    }

    setBuscando(true);

    try {
      const vehicleDocRef = doc(db, 'vehiculos', dominio.toUpperCase());
      const vehicleDocSnap = await getDoc(vehicleDocRef);

      if (!vehicleDocSnap.exists()) {
        setSearchResult({ type: 'error', text: `Vehículo ${dominio.toUpperCase()} no encontrado en la flota.` });
        setShowSuggestModal(true);
        setBuscando(false);
        return;
      }

      const q = query(
        collection(db, "checklistLivianos"),
        where("dominio", "==", dominio.toUpperCase()),
        where("fluidsWasChecked", "==", true),
        orderBy("fecha", "desc"),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      let shouldShowFluidos = true;
      if (!querySnapshot.empty) {
        const ultimoCheckDeFluidos = querySnapshot.docs[0].data().fecha.toDate();
        const diffDias = (new Date().getTime() - ultimoCheckDeFluidos.getTime()) / (1000 * 3600 * 24);
        shouldShowFluidos = diffDias >= 10;
      }

      setShowFluidos(shouldShowFluidos);
      setIniciarChecklist(true);
    } catch (error) {
      console.error("Error al iniciar checklist:", error);
      toast.error("Error al buscar el vehículo. Intentá nuevamente.");
      setBuscando(false);
    }
  };

  const handleQuickSearch = async () => {
    if (dominio.trim() === '') return;
    setBuscando(true);
    setSearchResult({ type: 'info', text: 'Buscando...' });

    try {
      const docRef = doc(db, 'vehiculos', dominio.toUpperCase());
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const q = query(
          collection(db, "checklistLivianos"),
          where("dominio", "==", dominio.toUpperCase()),
          orderBy("fecha", "desc"),
          limit(1)
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const ultimo = querySnapshot.docs[0].data().fecha.toDate();
          const fechaFormateada = ultimo.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const horaFormateada = ultimo.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
          setSearchResult({ type: 'info', text: `Último control: ${fechaFormateada}, ${horaFormateada}` });
        } else {
          setSearchResult({ type: 'info', text: 'Vehículo registrado. Sin checklists previos.' });
        }
      } else {
        setSearchResult({ type: 'error', text: `Vehículo ${dominio.toUpperCase()} no encontrado.` });
        setShowSuggestModal(true);
      }
    } catch (error) {
      console.error("Error en búsqueda:", error);
      setSearchResult({ type: 'error', text: 'Error al buscar. Intentá nuevamente.' });
    }
    setBuscando(false);
  };

  if (iniciarChecklist) {
    return <ChecklistForm dominio={dominio} user={user} showFluidos={showFluidos} />;
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="logo-row">
          <Shield size={28} />
          <span className="header-brand">Fleet Check</span>
        </div>
        <div className="user-info">
          <span>{user.email}</span>
        </div>
        <button onClick={() => signOut(auth)} className="btn-logout" aria-label="Cerrar sesión">
          Salir
        </button>
      </header>

      <main className="home-content">
        <h1 className="home-title">Checklist Vehicular</h1>

        <div className="card home-card">
          <div className="card-body">
            <div className="form-group">
              <label htmlFor="dominio-input" className="form-label">Dominio del vehículo</label>
              <input
                id="dominio-input"
                type="text"
                className="form-input"
                value={dominio}
                onChange={(e) => setDominio(e.target.value.toUpperCase())}
                placeholder="Ej: ABC123"
                autoComplete="off"
              />
            </div>

            <button
              onClick={handleStartChecklist}
              disabled={!dominio.trim() || buscando}
              className="btn btn-success btn-full btn-lg"
            >
              {buscando ? (
                <><div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div> Verificando...</>
              ) : 'Iniciar Checklist'}
            </button>

            <button
              onClick={handleQuickSearch}
              disabled={!dominio.trim() || buscando}
              className="btn btn-ghost btn-full btn-sm"
              style={{ marginTop: 'var(--space-sm)' }}
            >
              Ver último control
            </button>

            {searchResult && (
              <div className={`search-result ${searchResult.type}`} role="status">
                {searchResult.text}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {showSuggestModal && (
        <SuggestVehicleModal
          dominio={dominio.toUpperCase()}
          onClose={() => setShowSuggestModal(false)}
        />
      )}
    </div>
  );
}

export default Home;
